/**
 * Tag Status — dedicated lifecycle view (PENDING / ON_HAND / DEPARTED
 * / RETRIEVED). Same backing data as the Status section on Manage
 * Tags but as its own page so operators can land on it from nav
 * without going through the "manage matches" flow.
 *
 * Visibility / actions: gated by tags.manage (vendor + platform).
 * Vendor sessions see only their own catalog items; platform sees
 * everything.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import LoadingSpinner from "../components/LoadingSpinner";
import Modal from "../components/Modal";
import TagHeatMap from "../components/tags/TagHeatMap";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import { useScopedBoxes } from "../hooks/useScopedBoxes";
import { fmtUtc } from "../lib/format";
import {
  type TagEvent,
  type TagStatus,
  type TagStatusRow,
  bulkRetrieveTags,
  fetchTagHistory,
  listTagStatuses,
  retrieveTag,
} from "../services/tagApi";

const PAGE_SIZE = 50;

const STATUS_PILL: Record<TagStatus, string> = {
  ON_HAND:     "bg-success-100 text-success-700",
  CHECKED_OUT: "bg-warning-200 text-warning-900",
  PENDING:     "bg-secondary-100 text-secondary-700",
  DEPARTED:    "bg-warning-100 text-warning-700",
  RETRIEVED:   "bg-primary-100 text-primary-700",
  UNKNOWN:     "bg-error-100 text-error-700",
};

const STATUS_OPTIONS: { value: "" | TagStatus; label: string }[] = [
  { value: "", label: "All statuses" },
  { value: "ON_HAND", label: "On hand" },
  { value: "CHECKED_OUT", label: "Checked out (manual)" },
  { value: "PENDING", label: "Pending (never scanned)" },
  { value: "DEPARTED", label: "Departed" },
  { value: "RETRIEVED", label: "Retrieved" },
];

export const TagStatusPage: React.FC = () => {
  const { token } = useAuth();
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"" | TagStatus>("");
  // Location = the tag's current smartbox (what the Location column shows).
  const [locationFilter, setLocationFilter] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const { snapshot } = useScopedBoxes();

  // One option per smartbox the user can see, labelled "SB071 · 00B12
  // (Nabors)" so boxes on similarly named rigs can be told apart. Keyed by
  // smartbox_id because that is what a tag's location is recorded against.
  const locationOptions = useMemo(() => {
    const byId = new Map<number, string>();
    for (const b of snapshot?.boxes ?? []) {
      if (b.smartbox_id == null || byId.has(b.smartbox_id)) continue;
      const head = b.smartbox_code || b.scu_name;
      const rig = b.work_center ? ` · ${b.work_center}` : "";
      const cust = b.customer ? ` (${b.customer})` : "";
      byId.set(b.smartbox_id, `${head}${rig}${cust}`);
    }
    return Array.from(byId.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
  }, [snapshot]);
  const [data, setData] = useState<{ total: number; tags: TagStatusRow[] } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [retrieving, setRetrieving] = useState<TagStatusRow | null>(null);
  const [retrieveReason, setRetrieveReason] = useState("");
  const [saving, setSaving] = useState(false);

  // History modal — opens when a row's "history" button is clicked.
  // Loads the timeline lazily so we don't fan out 50× queries on page load.
  const [historyFor, setHistoryFor] = useState<TagStatusRow | null>(null);
  // Heat map — which antennas are seeing this tag right now. Offered only for
  // ON_HAND rows: a departed tag has no current position to draw.
  const [heatMapFor, setHeatMapFor] = useState<TagStatusRow | null>(null);
  const [historyEvents, setHistoryEvents] = useState<TagEvent[] | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [bulkSelected, setBulkSelected] = useState<Set<string>>(new Set());
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [bulkResult, setBulkResult] = useState<
    { requested: number; succeeded: number; failures: { epc: string; reason: string }[] } | null
  >(null);

  // Ignore stale/out-of-order responses: a slow earlier query must not
  // overwrite the results of a newer one (see ProductPicker for the pattern).
  const reqIdRef = useRef(0);

  const refresh = useCallback(() => {
    if (!token) return;
    const id = ++reqIdRef.current;
    setLoading(true);
    listTagStatuses(token, {
      q: query.trim() || undefined,
      status: statusFilter || undefined,
      smartboxId: locationFilter ?? undefined,
      page, pageSize: PAGE_SIZE,
    })
      .then((res) => {
        if (id !== reqIdRef.current) return;
        setData({ total: res.total, tags: res.tags });
        setBulkSelected((prev) => {
          const onPage = new Set(res.tags.map((t) => t.epc));
          const next = new Set<string>();
          prev.forEach((epc) => { if (onPage.has(epc)) next.add(epc); });
          return next;
        });
        setError(null);
      })
      .catch((e) => { if (id === reqIdRef.current) setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (id === reqIdRef.current) setLoading(false); });
  }, [token, query, statusFilter, locationFilter, page]);

  useEffect(() => {
    const t = setTimeout(refresh, 300);
    return () => clearTimeout(t);
  }, [refresh]);

  const doRetrieveOne = async (r: TagStatusRow): Promise<void> => {
    if (!token) return;
    setSaving(true);
    try {
      const out = await retrieveTag(r.epc, token, retrieveReason.trim() || undefined);
      if (!out.ok) {
        setError(out.reason ?? "Retrieval refused.");
      } else {
        setRetrieving(null);
        setRetrieveReason("");
        refresh();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const doBulkRetrieve = async (): Promise<void> => {
    if (!token || bulkSelected.size === 0) return;
    setSaving(true);
    try {
      const out = await bulkRetrieveTags(
        Array.from(bulkSelected), token, retrieveReason.trim() || undefined,
      );
      const failures = out.results
        .filter((r) => !r.ok)
        .map((r) => ({ epc: r.epc, reason: r.reason ?? "refused" }));
      setBulkResult({ requested: out.requested, succeeded: out.succeeded, failures });
      setBulkConfirm(false);
      setBulkSelected(new Set());
      setRetrieveReason("");
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const openHistory = async (r: TagStatusRow): Promise<void> => {
    if (!token) return;
    setHistoryFor(r);
    setHistoryEvents(null);
    setHistoryLoading(true);
    setError(null);
    try {
      const h = await fetchTagHistory(r.epc, token);
      setHistoryEvents(h.events);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setHistoryFor(null);
    } finally {
      setHistoryLoading(false);
    }
  };

  const toggleSelected = (epc: string): void => {
    setBulkSelected((prev) => {
      const next = new Set(prev);
      if (next.has(epc)) next.delete(epc);
      else next.add(epc);
      return next;
    });
  };

  const eligibleOnPage = (data?.tags ?? []).filter((t) => t.status === "ON_HAND");
  const allEligibleSelected =
    eligibleOnPage.length > 0 && eligibleOnPage.every((t) => bulkSelected.has(t.epc));
  const toggleSelectAll = (): void => {
    if (allEligibleSelected) {
      setBulkSelected(new Set());
    } else {
      const next = new Set<string>();
      eligibleOnPage.forEach((t) => next.add(t.epc));
      setBulkSelected(next);
    }
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <div className="p-4 md:p-6">
      <header className="mb-4">
        <h1 className="text-xl font-bold">Tag Status</h1>
        <p className="text-xs text-secondary-500 mt-1">
          Per-EPC lifecycle: <strong>Pending</strong> (matched in the catalog,
          never scanned), <strong>On hand</strong> (currently in a smartbox),{" "}
          <strong>Checked out</strong> (manually taken out — stays checked out
          until manually checked back in, regardless of scans),{" "}
          <strong>Departed</strong> (consumed via a confirmed departure),{" "}
          <strong>Retrieved</strong> (manually pulled by a vendor without a
          customer transaction). Derived from each tag's most recent movement
          record (arrival, departure, checkout, or retrieval).
        </p>
      </header>

      <div className="flex flex-col md:flex-row gap-2 md:items-end md:max-w-4xl mb-3">
        <input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setPage(1); }}
          placeholder="search by EPC, SKU, or name"
          className="flex-1 border border-secondary-300 rounded px-3 py-2 text-sm"
        />
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value as "" | TagStatus); setPage(1); }}
          className="border border-secondary-300 rounded px-2 py-2 text-sm bg-white"
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <select
          value={locationFilter ?? ""}
          onChange={(e) => {
            setLocationFilter(e.target.value ? Number(e.target.value) : null);
            setPage(1);
          }}
          aria-label="Filter by location"
          className="border border-secondary-300 rounded px-2 py-2 text-sm bg-white md:max-w-xs"
        >
          <option value="">All locations</option>
          {locationOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {error && (
        <div className="mb-3 text-sm text-error-700 bg-error-50 px-3 py-2 rounded">{error}</div>
      )}

      {bulkSelected.size > 0 && (
        <div className="mb-3 flex items-center gap-3 text-sm bg-primary-50 border border-primary-200 px-3 py-2 rounded">
          <span>{bulkSelected.size} selected</span>
          <Button size="sm" variant="outline" onClick={() => setBulkSelected(new Set())}>
            Clear
          </Button>
          <Button size="sm" onClick={() => setBulkConfirm(true)}>
            Retrieve selected
          </Button>
        </div>
      )}

      {loading && !data ? (
        <LoadingSpinner label="loading tags" />
      ) : (
        <>
          <div className="text-xs text-secondary-500 mb-2">
            {data?.total.toLocaleString() ?? 0} match{data?.total === 1 ? "" : "es"}
          </div>
          <div className="bg-white border border-secondary-200 rounded-md overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary-50 text-secondary-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="px-3 py-2 w-8">
                    <input
                      type="checkbox"
                      checked={allEligibleSelected}
                      onChange={toggleSelectAll}
                      disabled={eligibleOnPage.length === 0}
                      title={
                        eligibleOnPage.length === 0
                          ? "no ON_HAND tags on this page"
                          : "select all ON_HAND on this page"
                      }
                    />
                  </th>
                  <th className="text-left px-3 py-2">EPC</th>
                  <th className="text-left px-3 py-2">Product</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Location</th>
                  <th className="text-right px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(data?.tags ?? []).map((t) => {
                  const canRetrieve = t.status === "ON_HAND";
                  return (
                    <tr key={t.epc} className="border-t border-secondary-100">
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={bulkSelected.has(t.epc)}
                          onChange={() => toggleSelected(t.epc)}
                          disabled={!canRetrieve}
                          title={canRetrieve ? "select for bulk retrieve" : "only ON_HAND tags can be retrieved"}
                        />
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{t.epc}</td>
                      <td className="px-3 py-2">
                        <div className="font-mono">{t.sku ?? `#${t.item_id}`}</div>
                        {t.name && <div className="text-xs text-secondary-500">{t.name}</div>}
                        {t.vendor_name && (
                          <div className="text-[11px] text-secondary-400">{t.vendor_name}</div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <span className={"text-[11px] px-1.5 py-0.5 rounded " + STATUS_PILL[t.status]}>
                          {t.status}
                        </span>
                        {t.last_event_utc && (
                          <div className="text-[10px] text-secondary-500 mt-0.5">
                            {fmtUtc(t.last_event_utc)}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-secondary-700">
                        {t.current_smartbox_code ? (
                          <>
                            <div className="font-medium">{t.current_smartbox_code}</div>
                            {t.current_scu_name && (
                              <div className="text-[10px] text-secondary-400 font-mono">
                                {t.current_scu_name}
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="text-secondary-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => void openHistory(t)}
                          className="text-xs text-primary-600 hover:underline"
                        >
                          history
                        </button>
                        {t.status === "ON_HAND" && (
                          <button
                            type="button"
                            onClick={() => setHeatMapFor(t)}
                            className="ml-3 text-xs text-primary-600 hover:underline"
                          >
                            heat map
                          </button>
                        )}
                        {canRetrieve && (
                          <button
                            type="button"
                            onClick={() => { setRetrieving(t); setRetrieveReason(""); }}
                            className="ml-3 text-xs text-primary-600 hover:underline"
                          >
                            retrieve
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {data && data.tags.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-3 py-6 text-center text-secondary-500">
                      No tags match the current filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-3 text-sm">
              <Button
                type="button" variant="outline" size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                ← Prev
              </Button>
              <span className="text-xs text-secondary-500">
                Page {page} of {totalPages}
              </span>
              <Button
                type="button" variant="outline" size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next →
              </Button>
            </div>
          )}
        </>
      )}

      {/* Antenna heat map — where the box is seeing this tag. */}
      <Modal
        open={!!heatMapFor}
        onClose={() => setHeatMapFor(null)}
        title={heatMapFor ? `Antennas: ${heatMapFor.epc}` : "Antennas"}
      >
        {heatMapFor && <TagHeatMap epc={heatMapFor.epc} />}
      </Modal>

      {/* History timeline */}
      <Modal
        open={!!historyFor}
        onClose={() => { setHistoryFor(null); setHistoryEvents(null); }}
        title={historyFor ? `Lifecycle: ${historyFor.epc}` : "Lifecycle"}
        widthClass="max-w-2xl"
      >
        {historyFor && (
          <div className="space-y-3">
            <div className="text-xs text-secondary-500">
              {historyFor.sku ?? `#${historyFor.item_id}`}
              {historyFor.name && <span className="ml-1">— {historyFor.name}</span>}
            </div>
            {historyLoading ? (
              <LoadingSpinner label="loading timeline" />
            ) : !historyEvents || historyEvents.length === 0 ? (
              <div className="text-sm text-secondary-600">No events recorded for this tag.</div>
            ) : (
              <ol className="border-l border-secondary-200 ml-2">
                {historyEvents.map((ev, i) => {
                  const tone =
                    ev.kind === "MATCH"          ? "bg-secondary-200 text-secondary-700" :
                    ev.kind === "BASELINE"       ? "bg-primary-100 text-primary-700" :
                    ev.kind === "ARRIVAL"        ? "bg-success-100 text-success-700" :
                    ev.kind === "DEPARTURE"      ? "bg-warning-100 text-warning-700" :
                    ev.kind === "DEPARTURE_VOID" ? "bg-error-100 text-error-700 line-through" :
                    ev.kind === "RETRIEVAL"      ? "bg-primary-100 text-primary-700" :
                    ev.kind === "REVERSAL"       ? "bg-secondary-100 text-secondary-700" :
                                                   "bg-secondary-100 text-secondary-700";
                  const kindLabel = ev.kind === "DEPARTURE_VOID" ? "VOIDED DEPARTURE" : ev.kind;
                  return (
                    <li key={i} className="relative pl-4 pb-3">
                      <span className="absolute -left-1.5 top-1.5 w-3 h-3 rounded-full bg-white border border-secondary-300" />
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className={"text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wide " + tone}>
                          {kindLabel}
                        </span>
                        <span className="text-xs text-secondary-700">
                          {ev.event_time_utc ? fmtUtc(ev.event_time_utc) : "—"}
                        </span>
                        {ev.qty !== 0 && (
                          <span className={"text-xs font-mono " + (ev.qty > 0 ? "text-success-700" : "text-error-700")}>
                            {ev.qty > 0 ? `+${ev.qty}` : ev.qty}
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 text-xs text-secondary-600">
                        {ev.smartbox_code && (
                          <span className="font-medium">{ev.smartbox_code}</span>
                        )}
                        {ev.scu_name && (
                          <span className="ml-2 font-mono text-[10px] text-secondary-400">{ev.scu_name}</span>
                        )}
                        {ev.actor && (
                          <span className="ml-2">by {ev.actor}</span>
                        )}
                        {ev.related_departure_ticket_id != null && (
                          <span className="ml-2 text-[10px] text-secondary-500">
                            (departure ticket #{ev.related_departure_ticket_id})
                          </span>
                        )}
                      </div>
                      {ev.notes && (
                        <div className="mt-0.5 text-[11px] text-secondary-500 italic">{ev.notes}</div>
                      )}
                    </li>
                  );
                })}
              </ol>
            )}
            <div className="flex justify-end pt-1">
              <Button size="sm" onClick={() => { setHistoryFor(null); setHistoryEvents(null); }}>
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Single retrieve confirm */}
      <Modal
        open={!!retrieving}
        onClose={() => { if (!saving) setRetrieving(null); }}
        title="Retrieve tag"
        widthClass="max-w-md"
      >
        {retrieving && (
          <div className="space-y-4">
            <p className="text-sm text-secondary-700">
              Remove <span className="font-mono">{retrieving.epc}</span> (
              {retrieving.sku ?? `#${retrieving.item_id}`}) from{" "}
              <strong>{retrieving.current_smartbox_code ?? "the current smartbox"}</strong>{" "}
              <em>without</em> recording a customer departure / consumption?
            </p>
            <p className="text-xs text-secondary-500">
              A manual RETRIEVAL ledger entry is recorded for the audit trail. The tag's
              status flips to RETRIEVED; if scanned again it returns to ON_HAND automatically.
            </p>
            <label className="block">
              <span className="block text-xs uppercase tracking-wide text-secondary-500 mb-1">
                Reason (optional)
              </span>
              <input
                value={retrieveReason}
                onChange={(e) => setRetrieveReason(e.target.value)}
                placeholder="e.g. defective, restocking, ..."
                className="w-full border border-secondary-300 rounded px-2 py-1.5 text-sm"
              />
            </label>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" size="sm" disabled={saving}
                onClick={() => setRetrieving(null)}>
                Cancel
              </Button>
              <Button type="button" size="sm" disabled={saving}
                onClick={() => void doRetrieveOne(retrieving)}>
                {saving ? "Retrieving…" : "Retrieve"}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Bulk confirm */}
      <Modal
        open={bulkConfirm}
        onClose={() => { if (!saving) setBulkConfirm(false); }}
        title={`Retrieve ${bulkSelected.size} tag${bulkSelected.size === 1 ? "" : "s"}`}
        widthClass="max-w-md"
      >
        <div className="space-y-4">
          <p className="text-sm text-secondary-700">
            About to retrieve {bulkSelected.size} ON_HAND tag
            {bulkSelected.size === 1 ? "" : "s"}. Each becomes a manual RETRIEVAL ledger
            entry — no customer departure / consumption is recorded.
          </p>
          <label className="block">
            <span className="block text-xs uppercase tracking-wide text-secondary-500 mb-1">
              Reason (optional, applied to all)
            </span>
            <input
              value={retrieveReason}
              onChange={(e) => setRetrieveReason(e.target.value)}
              placeholder="e.g. recall, end-of-life sweep, ..."
              className="w-full border border-secondary-300 rounded px-2 py-1.5 text-sm"
            />
          </label>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" size="sm" disabled={saving}
              onClick={() => setBulkConfirm(false)}>
              Cancel
            </Button>
            <Button type="button" size="sm" disabled={saving}
              onClick={() => void doBulkRetrieve()}>
              {saving ? "Retrieving…" : "Retrieve all"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Bulk result summary */}
      <Modal
        open={!!bulkResult}
        onClose={() => setBulkResult(null)}
        title="Retrieval result"
        widthClass="max-w-lg"
      >
        {bulkResult && (
          <div className="space-y-3">
            <p className="text-sm">
              {bulkResult.succeeded} of {bulkResult.requested} tag
              {bulkResult.requested === 1 ? "" : "s"} retrieved.
            </p>
            {bulkResult.failures.length > 0 && (
              <div className="border border-warning-200 bg-warning-50 rounded p-2 text-xs">
                <div className="font-semibold text-warning-800 mb-1">
                  {bulkResult.failures.length} refused
                </div>
                <ul className="space-y-1">
                  {bulkResult.failures.slice(0, 12).map((f) => (
                    <li key={f.epc}><span className="font-mono">{f.epc}</span>: {f.reason}</li>
                  ))}
                  {bulkResult.failures.length > 12 && (
                    <li>… +{bulkResult.failures.length - 12} more</li>
                  )}
                </ul>
              </div>
            )}
            <div className="flex justify-end">
              <Button size="sm" onClick={() => setBulkResult(null)}>OK</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default TagStatusPage;
